import React, { useState, useMemo, useEffect } from 'react';
import { MicroTrend } from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/useMicroTrendsListData';
import {
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import { useEntityMetadataEnhancer } from 'src/components/AtlasV2Components/Hooks/useEntityMetadataEnhancer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import Box from '@mui/material/Box';
import colors from 'src/utils/colors';
import { useMicroTrendsTimePeriodStore } from './MicroTrendStore';
import { useDorisData } from 'src/utils/Hooks/useDorisData';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { RightArrow } from 'src/components/SvgIcons/SvgIcons';
import { MicroTrendsDetailModal } from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendsDetailModal';
import _capitalize from 'lodash/capitalize';
import { MicroTrendsChartLoading } from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendsCardLoading';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { formatMetric, safeDivide } from 'src/utils/app';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import useDorisMetricByWeekIdBypassFilters from 'src/serverProxy/useDorisMetricByWeekIdBypassFilters';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import InlineClampWithViewMore from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/InlineClampWithViewMore';
import { titleCase } from 'src/utils/stringFormatting';
import { timestampToWeekId, weekIdToTimestamp, getPreviousWeekId } from 'src/utils/dateUtils';
import { getPercentageAxisConfig } from 'src/utils/chartUtils';
import Highcharts from 'highcharts';
import {
  useMicroTrendSubcategoryDataStore,
  generateSubcategoryCacheKey
} from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendSubcategoryDataStore';
import { TooltipIconComponent } from 'src/components/BeaconV2Components/ExperimentalLayout/ProductGrowthPage';
import { StacklineTheme, useStacklineTheme } from '@stackline/ui';
import {
  getColorForMetricChangeValue,
  getIconForMetricChange
} from 'src/components/BeaconV2Components/utils/chartStyles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

export const MicroTrendCustomTooltip = styled(({ className, title, children, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} title={title} arrow>
    {children}
  </Tooltip>
))(({ stacklineTheme }: { stacklineTheme: StacklineTheme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: stacklineTheme.colors.secondaryGhost,
    padding: '8px 12px 8px 12px'
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: stacklineTheme.colors.secondaryGhost
  }
}));

export const MicroTrendsCard = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      border="1px solid"
      borderColor={colors.lightestGrey2}
      borderRadius="8px"
      padding="40px"
      gap={16}
      height="380px"
    >
      {children}
    </Flex>
  );
};

export const MicroTrendsCardSummary = ({
  trends,
  selectedTrendIndex
}: {
  trends: MicroTrend[];
  selectedTrendIndex: number;
}) => {
  const selectedTrend = trends[selectedTrendIndex];

  const { beaconNavigator } = useBeaconNavigator();

  const isBrandTrend = selectedTrend.type === 1;
  const { getEntityDisplayName } = useEntityMetadataEnhancer();
  const categoryName = getEntityDisplayName(selectedTrend.subcategoryId, 'subcategoryId');
  const [trendDetailsPopup, setTrendDetailsPopup] = useState<{
    trend: MicroTrend;
    open: boolean;
    trendIndex: number;
  } | null>(null);

  const microTrendTimePeriod = useMicroTrendsTimePeriodStore((state) => state.timePeriod);
  const { endWeek } = microTrendTimePeriod;

  // Find the longest trend (the one with the most weeks)
  const longestTrend = React.useMemo(() => {
    if (!trends || trends.length === 0) return selectedTrend;
    return trends.reduce((longest, current) => (current.numWeeks > longest.numWeeks ? current : longest), trends[0]);
  }, [trends, selectedTrend]);

  // Memoize params to keep identity stable and avoid unnecessary refetches
  // Use only the longest trend's ID
  const skipAdditionParams = React.useMemo(() => {
    return {
      trendIds: [longestTrend.trendId],
      startWeekId: endWeek,
      endWeekId: endWeek,
      // Strip everything except categoryIds and trendIds (plus required request scaffolding)
      interceptBaseParams: (payload: Record<string, any>) => {
        const { categoryIds, trendIds, metricType, retailerId, startWeekId, endWeekId, resultCount } = payload;
        return {
          metricType,
          retailerId,
          startWeekId,
          endWeekId,
          resultCount,
          categoryIds: Array.isArray(categoryIds) ? categoryIds : undefined,
          trendIds: Array.isArray(trendIds) ? trendIds : undefined
        };
      }
    };
  }, [longestTrend.trendId, endWeek]);

  const { isLoading: isSkuCountLoading, data: skuCountData } = useDorisData({
    queryKey: 'micro-trends-sku-count',
    metricType: DorisMetricTypeEnum.ProductsWithSalesCount,
    resultCount: 1,
    skipComparison: true,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    additionalParams: skipAdditionParams,
    queryOptions: {
      retry: 0,
      refetchOnWindowFocus: false
    }
  });

  const trendTitle = isBrandTrend
    ? `${selectedTrend.brandName} - ${categoryName}`
    : `${titleCase(selectedTrend.tagName)} (${_capitalize(selectedTrend.tagValue)}) - ${categoryName}`;

  const href = React.useMemo(() => {
    return beaconNavigator
      .setPathname(`/microtrend/${selectedTrend.trendId}`)
      .updateSearchParams({ tab: 'scorecardV2', subtab: 'all' })
      .build();
  }, [beaconNavigator, selectedTrend.trendId]);

  return (
    <Flex flexDirection="column" gap={8} width="328px" justifyContent="center">
      <Flex alignItems="center" gap={8}>
        {trends.map((trend) => (
          <Flex
            key={trend.trendId}
            alignItems="center"
            gap="xs"
            sx={{
              backgroundColor: '#E6E7E9'
              // cursor: 'pointer', // TODO:: ENABLE THIS WHEN PRODUCT DECIDE IF WE WANT TO SHOW MULTIPLE
              // '&:hover': {
              //   backgroundColor: colors.lightGrey
              // }
            }}
            padding="4px 8px"
            borderRadius="4px"
            height="24px"
            width="fit-content"
            // onClick={() => onTrendSelect(index)} // TODO:: ENABLE THIS WHEN PRODUCT DECIDE IF WE WANT TO SHOW MULTIPLE
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: colors.darkBlue,
                lineHeight: '16px',
                letterSpacing: '0.96px'
              }}
            >
              {trend.numWeeks} WEEKS
            </div>
          </Flex>
        ))}
      </Flex>

      <Flex flexDirection="column" width="328px">
        <Text variant="h4" sx={{ lineHeight: '28px', letterSpacing: '0.08px' }} truncateLines={3}>
          {trendTitle}
        </Text>
      </Flex>

      <Box sx={{ fontSize: '14px', lineHeight: '20px', letterSpacing: '0.08px', color: 'text.primary' }}>
        <InlineClampWithViewMore
          text={selectedTrend.summary}
          maxLines={3}
          onViewMore={() => {
            setTrendDetailsPopup({ trend: selectedTrend, open: true, trendIndex: selectedTrendIndex });
          }}
        />
      </Box>

      <Flex marginTop="8px">
        <Text
          sx={{
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: '1.2px',
            lineHeight: '20px',
            textDecoration: 'uppercase',
            color: colors.grey
          }}
        >
          {isSkuCountLoading ? (
            <SlSkeleton variant="rounded" height={20} width={120} />
          ) : (
            `${skuCountData?.records?.[0]?.value ?? 0} PRODUCTS`
          )}
        </Text>
      </Flex>

      <a
        href={href}
        style={{
          marginTop: '24px',
          color: colors.darkBlue,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: '24px',
          letterSpacing: '0.08px',
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          textDecoration: 'none'
        }}
        aria-label="Learn more about this microtrend"
        onClick={(e) => {
          e.preventDefault();
          const trendId = selectedTrend.trendId;
          beaconNavigator.setPathname(`/microtrend/${trendId}`);
          beaconNavigator.updateSearchParams({ tab: 'scorecardV2', subtab: 'all' }).go();
        }}
      >
        <Text
          sx={{
            color: colors.darkBlue,
            fontSize: 16,
            fontWeight: 700,
            lineHeight: '24px',
            letterSpacing: '0.08px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          Learn more about this microtrend <RightArrow style={{ height: '14px', width: '14px', marginLeft: '5px' }} />
        </Text>
      </a>

      <MicroTrendsDetailModal
        open={(trendDetailsPopup?.trend && trendDetailsPopup?.open) || false}
        onClose={() => setTrendDetailsPopup(null)}
        trendData={trendDetailsPopup?.trend}
        skuCount={skuCountData?.records?.[0]?.value}
        currentIndex={trendDetailsPopup?.trendIndex || 0}
        parentTrends={trends}
        // TODO:: ENABLE THESE WHEN PRODUCT DECIDE IF WE WANT TO SHOW MULTIPLE
        // totalTrends={trends.length}
        // onNavigate={(direction) => {
        //   if (!trendDetailsPopup) return;
        //   const newIndex =
        //     direction === 'next'
        //       ? Math.min(trendDetailsPopup.trendIndex + 1, trends.length - 1)
        //       : Math.max(trendDetailsPopup.trendIndex - 1, 0);
        //   setTrendDetailsPopup({
        //     ...trendDetailsPopup,
        //     trend: trends[newIndex],
        //     trendIndex: newIndex
        //   });
        //   onTrendSelect(newIndex);
        // }}
        onView={() => {
          if (trendDetailsPopup?.trend) {
            beaconNavigator.setPathname(`/microtrend/${trendDetailsPopup.trend.trendId}`);
            beaconNavigator.updateSearchParams({ tab: 'scorecardV2', subtab: 'all' }).go();
          }
        }}
      />
    </Flex>
  );
};

export const TrendGroupCard = ({ trendGroup }: { trendGroup: { trendGroupId: string; trends: MicroTrend[] } }) => {
  const sortedByWeekNumber = trendGroup?.trends.sort((a, b) => a.numWeeks - b.numWeeks);
  const selectedTrendIndex = sortedByWeekNumber.length - 1;

  return (
    <Flex flexDirection="column" gap={32}>
      <MicroTrendsCard>
        <MicroTrendsCardSummary trends={sortedByWeekNumber} selectedTrendIndex={selectedTrendIndex} />
        <Flex marginLeft="40px">
          <MicroTrendsChart trend={sortedByWeekNumber[selectedTrendIndex]} trends={sortedByWeekNumber} />
        </Flex>
      </MicroTrendsCard>
    </Flex>
  );
};

export const MicroTrendsChart = ({ trend, trends }: { trend: MicroTrend; trends: MicroTrend[] }) => {
  const theme = useStacklineTheme();

  // Find the longest trend to determine the week range
  const longestTrend = useMemo(() => {
    if (!trends || trends.length === 0) return trend;
    return trends.reduce((longest, current) => (current.numWeeks > longest.numWeeks ? current : longest), trends[0]);
  }, [trends, trend]);

  const { startWeekId, endWeekId } = longestTrend;
  const microTrendTimePeriod = useMicroTrendsTimePeriodStore((state) => state.timePeriod);
  const { startWeek: mainStartWeek, endWeek: mainEndWeek } = microTrendTimePeriod;

  // Cache store hooks
  const { getSubcategoryData, setSubcategoryData, isDataFresh } = useMicroTrendSubcategoryDataStore();

  // Generate cache key (only need main period since we skip comparison)
  const subcategoryCacheKey = useMemo(
    () => generateSubcategoryCacheKey(trend.subcategoryId, mainStartWeek, mainEndWeek),
    [trend.subcategoryId, mainStartWeek, mainEndWeek]
  );

  // Check if we have cached data
  const cachedData = getSubcategoryData(subcategoryCacheKey);
  const shouldFetchSubcategory = !cachedData || !isDataFresh(subcategoryCacheKey);

  // Calculate previous year week IDs if trend is 52 weeks
  const { previousYearStartWeek, previousYearEndWeek, shouldFetchPreviousYear } = useMemo(() => {
    if (trend.numWeeks !== 52) {
      return { previousYearStartWeek: null, previousYearEndWeek: null, shouldFetchPreviousYear: false };
    }

    // Calculate previous year by going back 52 weeks from start and end
    const prevStartWeek = getPreviousWeekId(startWeekId, 52);
    const prevEndWeek = getPreviousWeekId(endWeekId, 52);

    return {
      previousYearStartWeek: prevStartWeek,
      previousYearEndWeek: prevEndWeek,
      shouldFetchPreviousYear: true
    };
  }, [trend.numWeeks, startWeekId, endWeekId]);

  // Calculate prior period week IDs (same duration as trend, ending just before trend starts)
  const { priorPeriodStartWeek, priorPeriodEndWeek } = useMemo(() => {
    // Prior period ends one week before the trend starts
    const priorEndWeek = getPreviousWeekId(startWeekId, 1);
    // Prior period starts N weeks before the prior end week (where N is the trend duration - 1)
    const priorStartWeek = getPreviousWeekId(priorEndWeek, trend.numWeeks - 1);

    return {
      priorPeriodStartWeek: priorStartWeek,
      priorPeriodEndWeek: priorEndWeek
    };
  }, [startWeekId, trend.numWeeks]);

  // Fetch trend retail sales data (numerator)
  const metricByValues = useDorisMetricByWeekIdBypassFilters({
    queryKey: `trend-${trend.trendId}`,
    metricType: DorisMetricTypeEnum.RetailSales,
    resultCount: 1200,
    skipComparison: true,
    metricTypeDisplay: METRICTYPE.MONEY,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    overrideStartWeekId: mainStartWeek,
    overrideEndWeekId: mainEndWeek,
    baseParamsBuilder: (params) => {
      return {
        ...params,
        trendIds: [trend.trendId]
      };
    },
    allowedFilterKeys: ['trendIds']
  });

  // Fetch previous year trend data if needed (only when trend.numWeeks is 52)
  const previousYearMetricByValues = useDorisMetricByWeekIdBypassFilters({
    queryKey: `trend-${trend.trendId}-prev-year`,
    metricType: DorisMetricTypeEnum.RetailSales,
    resultCount: 1200,
    skipComparison: true,
    metricTypeDisplay: METRICTYPE.MONEY,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    overrideStartWeekId: previousYearStartWeek || mainStartWeek,
    overrideEndWeekId: previousYearEndWeek || mainEndWeek,
    baseParamsBuilder: (params) => {
      return {
        ...params,
        trendIds: [trend.trendId]
      };
    },
    allowedFilterKeys: ['trendIds']
  });

  // Fetch subcategory retail sales data (denominator) - with caching
  const { data: subcategoryMainData, isLoading: isSubcategoryMainLoading } = useDorisData({
    queryKey: `subcategory-${trend.subcategoryId}-main`,
    metricType: DorisMetricTypeEnum.RetailSales,
    fieldName: 'weekId',
    resultCount: 1200,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    customTimePeriods: {
      startWeekId: mainStartWeek,
      endWeekId: mainEndWeek
    },
    additionalParams: {
      subcategoryIds: [trend.subcategoryId],
      interceptBaseParams: (payload: Record<string, unknown>) => {
        // Strip all filters except subcategoryIds
        const { subcategoryIds, metricType, retailerId, startWeekId, endWeekId, resultCount } = payload;
        return {
          metricType,
          retailerId,
          startWeekId,
          endWeekId,
          resultCount,
          subcategoryIds
        };
      }
    },
    enabled: shouldFetchSubcategory,
    skipComparison: true
  });

  // Fetch previous year subcategory data if needed (only when trend.numWeeks is 52)
  const { data: subcategoryPreviousYearData, isLoading: isSubcategoryPreviousYearLoading } = useDorisData({
    queryKey: `subcategory-${trend.subcategoryId}-prev-year`,
    metricType: DorisMetricTypeEnum.RetailSales,
    fieldName: 'weekId',
    resultCount: 1200,
    methodName: DorisMetricEndPointEnum.SalesEndPoint,
    customTimePeriods: {
      startWeekId: previousYearStartWeek || mainStartWeek,
      endWeekId: previousYearEndWeek || mainEndWeek
    },
    additionalParams: {
      subcategoryIds: [trend.subcategoryId],
      interceptBaseParams: (payload: Record<string, unknown>) => {
        // Strip all filters except subcategoryIds
        const { subcategoryIds, metricType, retailerId, startWeekId, endWeekId, resultCount } = payload;
        return {
          metricType,
          retailerId,
          startWeekId,
          endWeekId,
          resultCount,
          subcategoryIds
        };
      }
    },
    enabled: shouldFetchPreviousYear,
    skipComparison: true
  });

  // Store subcategory data in cache when fetched
  useEffect(() => {
    if (subcategoryMainData && !isSubcategoryMainLoading) {
      setSubcategoryData(subcategoryCacheKey, {
        records: subcategoryMainData.records || [],
        timestamp: Date.now()
      });
    }
  }, [subcategoryMainData, isSubcategoryMainLoading, subcategoryCacheKey, setSubcategoryData]);

  const { primaryData: trendPrimaryData, isLoading: isTrendLoading } = metricByValues;
  const { primaryData: trendPreviousYearPrimaryData, isLoading: isTrendPreviousYearLoading } =
    previousYearMetricByValues;

  // Convert weekIds to timestamps for comparison
  const trendStartTimestamp = useMemo(() => weekIdToTimestamp(startWeekId), [startWeekId]);
  const trendEndTimestamp = useMemo(() => weekIdToTimestamp(endWeekId), [endWeekId]);
  const priorPeriodStartTimestamp = useMemo(() => weekIdToTimestamp(priorPeriodStartWeek), [priorPeriodStartWeek]);
  const priorPeriodEndTimestamp = useMemo(() => weekIdToTimestamp(priorPeriodEndWeek), [priorPeriodEndWeek]);

  // Calculate SHARE data (trend retail sales / subcategory retail sales)
  const { shareChartData, currentTrendValue, averageShareValue, priorPeriodAverageShareValue } = useMemo(() => {
    // Get subcategory data from cache or fresh fetch
    const subcategoryData = cachedData || {
      records: subcategoryMainData?.records || []
    };

    // Create maps for quick lookup
    const subcategoryMap = new Map<number, number>(
      subcategoryData.records.map((r) => {
        const timestamp = weekIdToTimestamp(r.weekId);
        const weekId = timestampToWeekId(timestamp);
        return [weekId, r.value];
      })
    );

    const calculateShareForDataPoints = (dataPoints: number[][]): number[][] => {
      return dataPoints.map(([timestamp, trendValue]) => {
        const weekId = timestampToWeekId(timestamp);
        const subcategoryValue = subcategoryMap.get(weekId) ?? 0;
        const shareValue = safeDivide(trendValue, subcategoryValue);
        return [timestamp, shareValue]; // Keep as decimal - PERCENT formatter will handle conversion
      });
    };

    const primaryShareData = calculateShareForDataPoints(trendPrimaryData);

    const currentTrendValue = trendPrimaryData.at(-1)?.[1] ?? 0;

    // Calculate average share for the trend period (trend.startWeekId to trend.endWeekId)
    // Filter data points within the trend period
    const trendPeriodData = trendPrimaryData.filter(
      ([timestamp]) => timestamp >= trendStartTimestamp && timestamp <= trendEndTimestamp
    );

    // Sum up trend values and subcategory values for the trend period
    let totalTrendValue = 0;
    let totalSubcategoryValue = 0;

    trendPeriodData.forEach(([timestamp, trendValue]) => {
      const weekId = timestampToWeekId(timestamp);
      const subcategoryValue = subcategoryMap.get(weekId) ?? 0;
      totalTrendValue += trendValue;
      totalSubcategoryValue += subcategoryValue;
    });

    // Calculate average share as total trend / total subcategory (like market share calculation)
    const averageShareValue = safeDivide(totalTrendValue, totalSubcategoryValue);

    // Calculate prior period average share
    // For 52 week trends, use previous year data; for others, use data from current fetch
    let priorPeriodData: number[][];
    if (shouldFetchPreviousYear && trendPreviousYearPrimaryData && trendPreviousYearPrimaryData.length > 0) {
      // For 52 week trends, prior period is the previous year (same weeks, previous year)
      priorPeriodData = trendPreviousYearPrimaryData;
    } else {
      // For 13w and 26w trends, prior period data is in the current main period data
      priorPeriodData = trendPrimaryData.filter(
        ([timestamp]) => timestamp >= priorPeriodStartTimestamp && timestamp <= priorPeriodEndTimestamp
      );
    }

    // Add previous year subcategory data to map if we're using it for 52w trends
    if (shouldFetchPreviousYear && subcategoryPreviousYearData?.records) {
      subcategoryPreviousYearData.records.forEach((r) => {
        const timestamp = weekIdToTimestamp(r.weekId);
        const weekId = timestampToWeekId(timestamp);
        subcategoryMap.set(weekId, r.value);
      });
    }

    // Sum up prior period trend values and subcategory values
    let totalPriorTrendValue = 0;
    let totalPriorSubcategoryValue = 0;

    priorPeriodData.forEach(([timestamp, trendValue]) => {
      const weekId = timestampToWeekId(timestamp);
      const subcategoryValue = subcategoryMap.get(weekId) ?? 0;
      totalPriorTrendValue += trendValue;
      totalPriorSubcategoryValue += subcategoryValue;
    });

    const priorPeriodAverageShareValue = safeDivide(totalPriorTrendValue, totalPriorSubcategoryValue);

    return {
      shareChartData: {
        primary: primaryShareData
      },
      currentTrendValue,
      averageShareValue,
      priorPeriodAverageShareValue
    };
  }, [
    trendPrimaryData,
    trendPreviousYearPrimaryData,
    cachedData,
    subcategoryMainData,
    subcategoryPreviousYearData,
    trendStartTimestamp,
    trendEndTimestamp,
    priorPeriodStartTimestamp,
    priorPeriodEndTimestamp,
    shouldFetchPreviousYear
  ]);

  const isLoading =
    isTrendLoading ||
    (shouldFetchSubcategory && isSubcategoryMainLoading) ||
    (shouldFetchPreviousYear && (isTrendPreviousYearLoading || isSubcategoryPreviousYearLoading));

  // Function to split data into solid and dotted segments
  const modifySeries = useMemo(
    () => (series: Highcharts.SeriesOptionsType[]) => {
      type DataPoint = [number, number] | Highcharts.PointOptionsObject;
      type SeriesDataArray = [number, number][];

      const splitSeriesByTrendRange = (
        seriesData: DataPoint[],
        color: string,
        zIndex?: number
      ): Highcharts.SeriesOptionsType[] => {
        if (!seriesData || seriesData.length === 0) return [];

        const beforeTrend: SeriesDataArray = [];
        const duringTrend: SeriesDataArray = [];
        const afterTrend: SeriesDataArray = [];

        seriesData.forEach((point) => {
          let timestamp: number;
          let value: number;

          if (Array.isArray(point)) {
            timestamp = point[0];
            value = point[1];
          } else {
            timestamp = (point.x as number) || 0;
            value = (point.y as number) || 0;
          }

          if (timestamp <= trendStartTimestamp) {
            beforeTrend.push([timestamp, value]);
          } else if (timestamp > trendStartTimestamp && timestamp <= trendEndTimestamp) {
            duringTrend.push([timestamp, value]);
          } else if (timestamp > trendEndTimestamp) {
            afterTrend.push([timestamp, value]);
          }
        });

        // Connect segments by adding boundary points
        if (beforeTrend.length > 0 && duringTrend.length > 0) {
          duringTrend.unshift(beforeTrend[beforeTrend.length - 1]);
        }
        if (duringTrend.length > 0 && afterTrend.length > 0) {
          afterTrend.unshift(duringTrend[duringTrend.length - 1]);
        }

        const result: Highcharts.SeriesOptionsType[] = [];

        // Before trend - dotted line
        if (beforeTrend.length > 0) {
          result.push({
            type: 'spline',
            data: beforeTrend,
            color,
            dashStyle: 'Dash',
            lineWidth: 2,
            marker: { enabled: false },
            enableMouseTracking: true,
            zIndex: zIndex || 1
          } as Highcharts.SeriesSplineOptions);
        }

        // During trend - solid line
        if (duringTrend.length > 0) {
          result.push({
            type: 'spline',
            data: duringTrend,
            color,
            dashStyle: 'Solid',
            lineWidth: 2,
            marker: { enabled: false },
            enableMouseTracking: true,
            zIndex: zIndex || 1
          } as Highcharts.SeriesSplineOptions);
        }

        // After trend - dotted line
        if (afterTrend.length > 0) {
          result.push({
            type: 'spline',
            data: afterTrend,
            color,
            dashStyle: 'Dot',
            lineWidth: 2,
            marker: { enabled: false },
            enableMouseTracking: true,
            zIndex: zIndex || 1
          } as Highcharts.SeriesSplineOptions);
        }

        return result;
      };

      const newSeries: Highcharts.SeriesOptionsType[] = [];

      // Process each series
      series.forEach((s) => {
        if (s.type === 'spline' && s.data && s.data.length > 0) {
          const color = s.color as string;
          const splineSeries = s as Highcharts.SeriesSplineOptions;
          const zIndex = splineSeries.zIndex;
          const splitSeries = splitSeriesByTrendRange(s.data as DataPoint[], color, zIndex);
          newSeries.push(...splitSeries);
        } else {
          // Keep non-spline series as is
          newSeries.push(s);
        }
      });

      return newSeries;
    },
    [trendStartTimestamp, trendEndTimestamp]
  );

  // Calculate dynamic Y-axis configuration based on data range (before early return)
  const chartOptionsOverride = useMemo(() => {
    const allShareValues = shareChartData.primary.map(([, value]) => value).filter((v) => !isNaN(v) && isFinite(v));

    if (allShareValues.length === 0) {
      return {};
    }

    const maxShare = Math.max(...allShareValues);
    const FIXED_TICK_COUNT = 4;

    // Get optimized yMax and step - automatically calculates closest 5% multiple above max
    const { yMax, step: finalStep } = getPercentageAxisConfig(maxShare, FIXED_TICK_COUNT);

    // Determine decimal places based on the step size
    const stepPercent = finalStep * 100;
    const maxPercent = maxShare * 100;

    const getDecimalPlacesForAxis = (): number => {
      if (stepPercent < 0.1) return 2;
      if (stepPercent < 1 || maxPercent <= 5) return 1;
      return 0;
    };

    const axisDecimalPlaces = getDecimalPlacesForAxis();

    return {
      yAxis: [
        {
          min: 0,
          max: yMax,
          startOnTick: true,
          endOnTick: true,
          tickPositioner: function () {
            const positions: number[] = [];
            for (let i = 0; i < FIXED_TICK_COUNT; i++) {
              positions.push(i * finalStep);
            }
            return positions;
          },
          labels: {
            formatter: function () {
              const value = this.value as number;

              if (value === 0) {
                return '0%';
              }

              return formatMetric(value, METRICTYPE.PERCENT, {
                decimalPlaces: axisDecimalPlaces,
                showFullValue: true
              });
            }
          }
        }
      ]
    };
  }, [shareChartData]);

  if (isLoading) {
    return <MicroTrendsChartLoading />;
  }

  const formattedAverageShareValue = formatMetric(averageShareValue, METRICTYPE.PERCENT, {
    decimalPlaces: 2,
    showFullValue: true
  });

  const valueChange = averageShareValue - priorPeriodAverageShareValue;

  const formattedPercentChange = formatMetric(
    safeDivide(valueChange, priorPeriodAverageShareValue),
    METRICTYPE.PERCENT,
    {
      decimalPlaces: 2,
      showFullValue: true
    }
  );
  const formattedValueChange = formatMetric(Math.abs(valueChange), METRICTYPE.PERCENT, {
    decimalPlaces: 2,
    showFullValue: true
  });

  return (
    <Flex flexDirection="column" width="592px" height="300px">
      <Flex alignItems="center" gap={8}>
        <Text
          variant="h4"
          fontWeight="700"
          sx={{
            color: colors.darkBlue,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '20px',
            letterSpacing: '1.2px',
            textTransform: 'uppercase'
          }}
        >
          Market share of retail sales
        </Text>
        <MicroTrendCustomTooltip
          title={
            <Flex maxWidth="318px" justifyContent="center" alignItems="center">
              <Text
                sx={{
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '18px',
                  letterSpacing: '0.08px'
                }}
              >
                This microtrend earned {formattedAverageShareValue} share of retail sales during the last{' '}
                {trend.numWeeks} week time period, which is {formattedValueChange}{' '}
                {valueChange > 0 ? 'higher' : 'lower'} than the prior period.
              </Text>
            </Flex>
          }
          stacklineTheme={theme}
          placement="right"
        >
          <TooltipIconComponent style={{ width: theme.spacing.md }} />
        </MicroTrendCustomTooltip>
      </Flex>

      <Flex alignItems="center">
        <Text
          sx={{
            color: colors.darkBlue,
            fontSize: 26,
            fontWeight: 700,
            lineHeight: '40px',
            letterSpacing: '0.08px',
            marginTop: '4px'
          }}
        >
          {formattedAverageShareValue}
        </Text>

        <Flex marginLeft="8px" alignItems="center">
          <Flex marginTop="5px">{getIconForMetricChange(valueChange)}</Flex>
          <Text
            variant="subtitle4"
            color={getColorForMetricChangeValue(valueChange)}
            sx={{
              lineHeight: '18px',
              letterSpacing: '0.08px',
              marginLeft: '4px'
            }}
          >
            {formattedValueChange}
          </Text>
          <Text
            variant="subtitle4"
            color={getColorForMetricChangeValue(valueChange)}
            sx={{
              lineHeight: '18px',
              letterSpacing: '0.08px',
              marginLeft: '4px'
            }}
          >
            ({formattedPercentChange})
          </Text>
        </Flex>
      </Flex>

      <Flex width="700px" height="212px" marginTop="26px">
        <SplineChart
          primaryData={shareChartData.primary}
          secondaryData={[]}
          metricType={METRICTYPE.PERCENT}
          modifySeries={modifySeries}
          chartOptionsOverride={chartOptionsOverride}
          buildPointsForTooltip={(points) => {
            const uniquePoints = new Map();
            points.forEach((point) => {
              if (!uniquePoints.has(point.x)) {
                uniquePoints.set(point.x, point);
              }
            });
            return Array.from(uniquePoints.values());
          }}
        />
      </Flex>
    </Flex>
  );
};
